import Handsontable from 'handsontable/base';
import { CellValueDetailedType, SimpleCellAddress } from 'hyperformula/es';
import { prop } from 'lodash/fp';

import {
    CellTypeNames,
    CellTypeObjectNames,
    DateStringFormatOptions,
    FormatOptions,
    TimeStringFormatOptions,
} from '../../../../../../common/table/CellTypeConstants';
import { CellTypeObject } from '../../../../../../common/table/TableTypes';
import { isCellOfType } from '../../../../../../common/table/utils/tableCellDataPropertyUtils';
import { getDefaultTypeObject } from '../../../../../../common/table/utils/tableCellTypeUtils';
import hyperFormulaInstance from '../../../manager/hyperFormulaInstance';

const FORMULAS_INFER_FIRST_ARG_CELL_TYPE = [
    'SUM',
    'SUMIF',
    'SUMIFS',
    'SUMSQ',
    'SUMX2MY2',
    'SUMX2PY2',
    'SUMXMY2',
    'SUMPRODUCT',
    'MIN',
    'MINA',
    'MINIFS',
    'MAX',
    'MAXA',
    'MAXIFS',
    'AVERAGE',
    'AVERAGEA',
    'AVERAGEIF',
];

const CELL_FORMULA_REGEX = /=([A-Za-z0-9]*)\(.*\)/;

/**
 * Infers the Milanote cell type for a formula cell based on HyperFormula metadata and precedents.
 */
export const inferFormulaCellType = (
    cellAddress: SimpleCellAddress,
    hotInstance: Handsontable,
): CellTypeObject | undefined => {
    const cellFormula = hyperFormulaInstance.getCellFormula(cellAddress);
    if (!cellFormula) return;

    const getCellTypeFromCellMeta = (cellType: CellTypeObjectNames) => {
        const { cellData } = hotInstance.getCellMeta(cellAddress.row, cellAddress.col) || {};
        return (isCellOfType(cellType)(cellData) && cellData?.type) || getDefaultTypeObject(cellType);
    };

    const hfValueDetailedType = hyperFormulaInstance.getCellValueDetailedType(cellAddress);
    switch (hfValueDetailedType) {
        case CellValueDetailedType.NUMBER_DATE:
            return {
                ...getCellTypeFromCellMeta(CellTypeNames.DATE_TIME),
                [FormatOptions.HAS_TIME]: false,
            };
        case CellValueDetailedType.NUMBER_DATETIME:
            return {
                ...getCellTypeFromCellMeta(CellTypeNames.DATE_TIME),
                [FormatOptions.HAS_TIME]: true,
                [FormatOptions.TIME_FORMAT]: TimeStringFormatOptions.TWELVE_HOUR,
            };
        case CellValueDetailedType.NUMBER_TIME:
            return {
                ...getCellTypeFromCellMeta(CellTypeNames.DATE_TIME),
                [FormatOptions.HAS_TIME]: true,
                [FormatOptions.TIME_FORMAT]: TimeStringFormatOptions.TWELVE_HOUR_SECONDS,
                [FormatOptions.TIME_IN_BRACKETS]: false,
                [FormatOptions.DATE_FORMAT]: DateStringFormatOptions.NONE,
            };
        case CellValueDetailedType.NUMBER_PERCENT:
            return getCellTypeFromCellMeta(CellTypeNames.PERCENTAGE);
        case CellValueDetailedType.NUMBER_CURRENCY:
            return getCellTypeFromCellMeta(CellTypeNames.CURRENCY);
        default: {
            const cellFormulaFnName = prop(1, cellFormula.match(CELL_FORMULA_REGEX))?.toUpperCase();
            const shouldInferBasedOnFirstPrecedentCell =
                cellFormulaFnName && FORMULAS_INFER_FIRST_ARG_CELL_TYPE.includes(cellFormulaFnName);

            if (!shouldInferBasedOnFirstPrecedentCell) return;

            let firstPrecedentCellAddress = prop(0, hyperFormulaInstance.getCellPrecedents(cellAddress));

            if (firstPrecedentCellAddress && 'start' in firstPrecedentCellAddress) {
                firstPrecedentCellAddress = firstPrecedentCellAddress.start;
            }

            return firstPrecedentCellAddress && inferFormulaCellType(firstPrecedentCellAddress, hotInstance);
        }
    }
};
